import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Icon } from '@iconify/react';
import { QRCodeSVG } from 'qrcode.react';
import showToast from '@/components/ui/Toast';
import { checkInvoiceStatus } from '@/services/bookingService';

export const ClientFieldPaymentInstructionPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state || {};

  const invoiceNumber = state.invoiceNumber || '';
  const arenaName = state.arenaName || 'HK 99 Mini Soccer';
  const fieldName = state.fieldName || 'Lapangan A · Sintetis';
  const formattedDate = state.formattedDate || '07 Agustus 2026';
  const durationText = state.durationText || '16.00 (1 jam)';
  const selectedMethod = state.selectedMethod || {
    id: 'qris',
    name: 'QRIS',
    category: 'instant',
    badgeText: 'QR',
    badgeBg: 'bg-black',
    badgeTextColor: 'text-white',
  };
  const grandTotal = state.grandTotal || 180000;

  // Countdown timer state
  const [timeLeft, setTimeLeft] = useState<number>(() => {
    if (state.paymentInfo?.payment_expires_at) {
      const expTime = new Date(state.paymentInfo.payment_expires_at).getTime();
      const now = Date.now();
      const diffSec = Math.floor((expTime - now) / 1000);
      return diffSec > 0 ? diffSec : 0;
    }
    return 24 * 3600 - 15;
  });
  const [isCopied, setIsCopied] = useState<boolean>(false);
  const [isCopiedAmount, setIsCopiedAmount] = useState<boolean>(false);
  const [isChecking, setIsChecking] = useState<boolean>(false);
  const [paymentStatus] = useState<'PENDING' | 'SUCCESS'>('PENDING');

  // Extract VA Number dynamically from Payment Gateway response payload
  const extractVaNumber = (stateObj: any): string => {
    const payload =
      stateObj?.paymentCheckoutPayload || stateObj?.paymentInfo?.payment_checkout_payload;
    if (payload) {
      const present = payload?.instructions?.present || payload?.actions?.present;
      if (Array.isArray(present) && present.length > 0) {
        const vaItem = present.find(
          (item: any) =>
            item?.kind === 'VIRTUAL_ACCOUNT_NUMBER' ||
            item?.kind === 'VA_NUMBER' ||
            item?.label?.toLowerCase().includes('virtual account') ||
            item?.label?.toLowerCase().includes('va')
        );
        if (vaItem && vaItem.value) return vaItem.value;
        if (present[0] && present[0].value) return present[0].value;
      }
      if (payload.va_number) return payload.va_number;
      if (payload.virtual_account_number) return payload.virtual_account_number;
    }
    if (stateObj?.paymentInfo?.va_number) return stateObj.paymentInfo.va_number;
    return stateObj?.vaNumber || stateObj?.va_number || '-';
  };

  const vaNumber = extractVaNumber(state);

  // Extract QR String dynamically from Payment Gateway response payload
  const extractQrValue = (stateObj: any): string => {
    const payload =
      stateObj?.paymentCheckoutPayload || stateObj?.paymentInfo?.payment_checkout_payload;
    if (payload) {
      const present = payload?.instructions?.present || payload?.actions?.present;
      if (Array.isArray(present) && present.length > 0) {
        const qrItem = present.find(
          (item: any) =>
            item?.kind === 'QR_STRING' ||
            item?.kind === 'QR_CODE' ||
            item?.kind === 'QRIS' ||
            item?.label?.toLowerCase().includes('qris') ||
            item?.label?.toLowerCase().includes('qr')
        );
        if (qrItem && (qrItem.value || qrItem.raw_value)) return qrItem.value || qrItem.raw_value;
      }
      if (payload.qr_string) return payload.qr_string;
      if (payload.qris_string) return payload.qris_string;
      if (payload.qr_code) return payload.qr_code;
      const redirect = payload?.instructions?.redirect;
      if (redirect?.deeplink_url || redirect?.web_url) {
        return redirect.deeplink_url || redirect.web_url;
      }
    }
    if (stateObj?.paymentInfo?.qr_string) return stateObj.paymentInfo.qr_string;
    if (stateObj?.paymentInfo?.qris_string) return stateObj.paymentInfo.qris_string;
    return stateObj?.qrString || stateObj?.qr_string || stateObj?.qris_string || '';
  };

  const qrValue = extractQrValue(state);

  useEffect(() => {
    if (paymentStatus === 'PENDING' && timeLeft > 0) {
      const timer = setInterval(() => {
        setTimeLeft((prev) => (prev > 0 ? prev - 1 : 0));
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [paymentStatus, timeLeft]);

  const formatCountdown = (seconds: number) => {
    const h = String(Math.floor(seconds / 3600)).padStart(2, '0');
    const m = String(Math.floor((seconds % 3600) / 60)).padStart(2, '0');
    const s = String(seconds % 60).padStart(2, '0');
    return `${h}:${m}:${s}`;
  };

  const handleCopyVa = () => {
    navigator.clipboard.writeText(vaNumber);
    setIsCopied(true);
    setTimeout(() => setIsCopied(false), 2000);
  };

  const handleCopyAmount = () => {
    navigator.clipboard.writeText(String(grandTotal));
    setIsCopiedAmount(true);
    setTimeout(() => setIsCopiedAmount(false), 2000);
  };

  const handleCheckStatus = async () => {
    if (!invoiceNumber) {
      showToast.error('Nomor invoice tidak ditemukan.');
      return;
    }
    setIsChecking(true);
    try {
      const res = await checkInvoiceStatus(invoiceNumber);
      if (res.success && (res.data?.is_paid || res.data?.status === 'PAID' || res.data?.status === 'COMPLETED')) {
        showToast.success('Pembayaran terverifikasi lunas!');
        navigate('/booking', { replace: true });
      } else {
        const currentStatus = res.data?.status || 'Belum Lunas';
        showToast.error(`Status pembayaran: ${currentStatus}. Silakan selesaikan pembayaran.`);
      }
    } catch (err) {
      showToast.error('Gagal mengecek status pembayaran. Silakan coba beberapa saat lagi.');
    } finally {
      setIsChecking(false);
    }
  };

  if (paymentStatus === 'SUCCESS') {
    return (
      <div className="bg-[#F8FAFC] min-h-screen pt-8 px-4 max-w-md mx-auto space-y-6 text-center">
        {/* Success Badge Banner */}
        <div className="space-y-3 pt-4">
          <div className="w-20 h-20 rounded-full bg-emerald-50 border-4 border-emerald-100 flex items-center justify-center mx-auto shadow-xs animate-bounce">
            <Icon icon="ph:check-circle-fill" className="w-12 h-12 text-emerald-500" />
          </div>
          <h1 className="text-xl font-extrabold text-[#0E1726]">Pembayaran Berhasil!</h1>
          <p className="text-xs text-gray-500 font-medium px-4">
            Transaksi kamu telah terverifikasi lunas. Jadwal booking kamu sudah aktif.
          </p>
        </div>

        {/* Invoice Summary Card */}
        <div className="bg-white rounded-3xl p-5 border border-gray-100 shadow-2xs space-y-4 text-left">
          <div className="flex items-center justify-between border-b border-gray-100 pb-3">
            <span className="text-xs text-gray-400 font-medium">No Order</span>
            <span className="text-xs font-mono font-extrabold text-emerald-600 bg-emerald-50 px-2.5 py-1 rounded-full border border-emerald-100">
              {invoiceNumber}
            </span>
          </div>

          <div className="space-y-1">
            <h3 className="text-sm font-extrabold text-[#0E1726]">{arenaName}</h3>
            <p className="text-xs text-gray-400 font-semibold">{fieldName}</p>
          </div>

          <div className="border-t border-b border-gray-100 py-3 space-y-2 text-xs">
            <div className="flex items-center justify-between">
              <span className="text-gray-400 font-medium">Tanggal Main</span>
              <span className="font-bold text-[#0E1726]">{formattedDate}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-gray-400 font-medium">Jam Sesi</span>
              <span className="font-bold text-[#0E1726]">{durationText}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-gray-400 font-medium">Metode Pembayaran</span>
              <span className="font-bold text-[#0E1726]">{selectedMethod.name}</span>
            </div>
          </div>

          <div className="flex items-center justify-between text-sm pt-1">
            <span className="font-extrabold text-[#0E1726]">Total Dibatalkan / Lunas</span>
            <span className="font-extrabold text-[#FC480D] text-base">
              Rp. {grandTotal.toLocaleString('id-ID')}
            </span>
          </div>
        </div>

        {/* Navigation Buttons */}
        <div className="space-y-2.5 pt-2">
          <button
            type="button"
            onClick={() => navigate('/booking')}
            className="w-full py-3.5 rounded-full bg-[#FC480D] hover:bg-[#D83B08] text-white text-xs font-extrabold shadow-md active:scale-98 transition-all cursor-pointer flex items-center justify-center gap-2"
          >
            <Icon icon="ph:ticket-bold" className="w-4 h-4" />
            <span>Lihat Tiket Booking Saya</span>
          </button>

          <button
            type="button"
            onClick={() => navigate('/')}
            className="w-full py-3.5 rounded-full bg-white border border-gray-200 text-gray-700 hover:bg-gray-50 text-xs font-extrabold active:scale-98 transition-all cursor-pointer"
          >
            Kembali ke Beranda
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-[#F8FAFC] min-h-screen pb-32 pt-2 px-4 max-w-md mx-auto space-y-4">
      {/* STATUS BANNER CARD */}
      <div className="bg-white rounded-3xl p-5 border border-amber-100 shadow-2xs text-center space-y-3">
        <span className="bg-amber-50 text-amber-600 font-extrabold text-xs px-3.5 py-1.5 rounded-full border border-amber-200 inline-flex items-center gap-1.5">
          <Icon icon="ph:clock-bold" className="w-4 h-4 text-amber-500 animate-spin" />
          Menunggu Pembayaran
        </span>

        <div className="space-y-1">
          <div className="text-xs text-gray-400 font-medium">Batas waktu pembayaran</div>
          <div className="text-xl font-extrabold text-[#0E1726] font-mono tracking-wider">
            {formatCountdown(timeLeft)}
          </div>
        </div>

        <div className="text-[11px] text-gray-400 font-medium border-t border-gray-100 pt-2 flex items-center justify-between">
          <span>No Order:</span>
          <span className="font-mono font-bold text-[#0E1726]">{invoiceNumber}</span>
        </div>
      </div>

      {/* PAYMENT METHOD INSTRUCTION DISPLAY */}
      <div className="bg-white rounded-3xl p-5 border border-gray-100 shadow-2xs space-y-4">
        <div className="flex items-center justify-between border-b border-gray-100 pb-3">
          <div className="flex items-center gap-2.5">
            <div
              className={`w-9 h-9 rounded-xl ${selectedMethod.badgeBg || 'bg-black'} ${selectedMethod.badgeTextColor || 'text-white'} font-extrabold text-xs flex items-center justify-center shadow-2xs`}
            >
              {selectedMethod.badgeText || 'QR'}
            </div>
            <div>
              <h3 className="text-xs font-extrabold text-[#0E1726]">{selectedMethod.name}</h3>
              <p className="text-[10px] text-gray-400 font-medium">Metode terpilih</p>
            </div>
          </div>
          <span className="text-xs font-extrabold text-[#FC480D]">
            Rp. {grandTotal.toLocaleString('id-ID')}
          </span>
        </div>

        {/* QRIS DISPLAY */}
        {selectedMethod.category === 'instant' || selectedMethod.id === 'qris' ? (
          <div className="text-center space-y-3 py-2">
            <div className="w-48 h-48 bg-white border-2 border-gray-100 rounded-2xl mx-auto p-3 flex flex-col items-center justify-center shadow-2xs relative group">
              {qrValue ? (
                <QRCodeSVG
                  value={qrValue}
                  size={168}
                  level="M"
                  includeMargin={true}
                />
              ) : (
                <div className="text-center p-2 space-y-1">
                  <Icon icon="ph:warning-circle-bold" className="w-8 h-8 text-amber-500 mx-auto" />
                  <p className="text-xs text-gray-500 font-medium">
                    Kode QRIS tidak tersedia.
                  </p>
                </div>
              )}
            </div>
            <p className="text-xs text-gray-500 font-medium px-2">
              Scan QRIS di atas menggunakan aplikasi BCA, Mandiri, GoPay, OVO, DANA, atau m-banking favorit kamu.
            </p>
          </div>
        ) : (
          /* VIRTUAL ACCOUNT / E-WALLET DISPLAY */
          <div className="space-y-3 py-1">
            <div className="space-y-1">
              <div className="text-xs text-gray-400 font-medium">Nomor Virtual Account</div>
              <div className="flex items-center justify-between bg-gray-50 p-3 rounded-2xl border border-gray-100">
                <span className="font-mono text-base font-extrabold text-[#0E1726] tracking-wider">
                  {vaNumber}
                </span>
                <button
                  type="button"
                  onClick={handleCopyVa}
                  className="text-xs font-extrabold text-[#FC480D] hover:underline flex items-center gap-1 cursor-pointer"
                >
                  <Icon icon={isCopied ? 'ph:check-bold' : 'ph:copy-bold'} className="w-4 h-4" />
                  <span>{isCopied ? 'Tersalin' : 'Salin'}</span>
                </button>
              </div>
            </div>

            <div className="space-y-1 pt-1">
              <div className="text-xs text-gray-400 font-medium">Total Pembayaran</div>
              <div className="flex items-center justify-between bg-orange-50/50 p-3 rounded-2xl border border-orange-100">
                <span className="text-base font-extrabold text-[#FC480D]">
                  Rp. {grandTotal.toLocaleString('id-ID')}
                </span>
                <button
                  type="button"
                  onClick={handleCopyAmount}
                  className="text-xs font-extrabold text-[#FC480D] hover:underline flex items-center gap-1 cursor-pointer"
                >
                  <Icon
                    icon={isCopiedAmount ? 'ph:check-bold' : 'ph:copy-bold'}
                    className="w-4 h-4"
                  />
                  <span>{isCopiedAmount ? 'Tersalin' : 'Salin Jumlah'}</span>
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* CARA PEMBAYARAN INSTRUCTIONS */}
      <div className="bg-white rounded-3xl p-4 sm:p-5 border border-gray-100 shadow-2xs space-y-3">
        <h3 className="text-xs font-extrabold text-[#0E1726]">Cara Pembayaran</h3>
        <ol className="space-y-2 text-xs text-gray-600 font-medium list-decimal list-inside leading-relaxed">
          <li>Buka aplikasi m-banking atau e-wallet pilihan kamu.</li>
          <li>
            Pilih menu <span className="font-bold text-[#0E1726]">Transfer / Pembayaran / QRIS</span>.
          </li>
          <li>
            {selectedMethod.id === 'qris'
              ? 'Scan kode QRIS yang tertera di atas.'
              : `Masukkan nomor Virtual Account: ${vaNumber}`}
          </li>
          <li>Periksa rincian pesanan dan selesaikan transaksi kamu.</li>
        </ol>
      </div>

      {/* ACTION BUTTONS */}
      <div className="space-y-2.5 pt-1">
        <button
          type="button"
          disabled={isChecking}
          onClick={handleCheckStatus}
          className="w-full py-3.5 rounded-full bg-white border border-gray-200 text-[#0E1726] hover:bg-gray-50 text-xs font-extrabold active:scale-98 transition-all cursor-pointer flex items-center justify-center gap-2 shadow-2xs"
        >
          {isChecking ? (
            <div className="w-4 h-4 border-2 border-[#FC480D] border-t-transparent rounded-full animate-spin" />
          ) : (
            <Icon icon="ph:arrow-clockwise-bold" className="w-4 h-4 text-[#FC480D]" />
          )}
          <span>{isChecking ? 'Mengecek Status...' : 'Cek Status Pembayaran'}</span>
        </button>

        <button
          type="button"
          onClick={() => navigate('/transactions')}
          className="w-full py-3.5 rounded-full bg-[#FC480D] hover:bg-[#D83B08] text-white text-xs font-extrabold shadow-md active:scale-98 transition-all cursor-pointer flex items-center justify-center gap-2"
        >
          <Icon icon="ph:receipt-bold" className="w-4 h-4" />
          <span>Lihat Riwayat Transaksi</span>
        </button>
      </div>
    </div>
  );
};
